import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import type { Coordinate2D, PoleAttachmentType, PoleFormatOption } from '../types';
import { calculateXforY } from '../utils/calculations';

// Style objects to replace Tailwind classes
const styles = {
  flexCol: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center',
  },
  textSmall: {
    fontSize: '12px',
  },
  textTiny: {
    fontSize: '10px',
  },
  fontSemibold: {
    fontWeight: 600,
  },
  fontBold: {
    fontWeight: 700,
  },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  rounded: {
    borderRadius: '4px',
  },
  roundedFull: {
    borderRadius: '9999px',
  },
  roundedLg: {
    borderRadius: '8px',
  },
  shadowLg: {
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
  border: {
    borderWidth: '1px',
    borderStyle: 'solid' as const,
    borderColor: '#e5e7eb',
  },
  borderBottom: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid' as const,
    borderBottomColor: '#e5e7eb',
  },
};

interface PoleAttachmentProps {
  attachment: PoleAttachmentType;
  currentImageId: string;
  initialImageScale: number;
  currentTransform: { x: number; y: number; scale: number };
  initialHorizontalOffset: number;
  initialVerticalOffset: number;
  imageContainerWidth: number;
  containerWidth: number;
  measurementFormat: PoleFormatOption;
  onPositionChange?: (attachmentId: string, imageId: string, newY: number) => void;
  onClick?: (attachmentId: string, event: React.MouseEvent) => void;
  labelYOffset?: number;
  opacity?: number;
  referenceLineTop?: Coordinate2D;
  referenceLineBottom?: Coordinate2D;
}

export const PoleAttachment: React.FC<PoleAttachmentProps> = ({
  attachment,
  currentImageId,
  initialImageScale,
  currentTransform,
  initialHorizontalOffset,
  initialVerticalOffset,
  imageContainerWidth,
  containerWidth,
  measurementFormat,
  onPositionChange,
  onClick,
  labelYOffset = 0,
  opacity = 1,
  referenceLineTop,
  referenceLineBottom,
}) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const [positions, setPositions] = useState<{ 
    textBox: { right: number; top: number }; 
    line: { left: number; right: number; top: number; verticalTop: number; verticalHeight: number } 
  }>({
    textBox: { right: 0, top: 0 },
    line: { left: 0, right: 0, top: 0, verticalTop: 0, verticalHeight: 0 }
  });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const imageCoords = useMemo(() => {
    // First check if we have images array (standard format)
    if (attachment.images) {
      const found = attachment.images.find((img) => img.imageId === currentImageId);
      if (found) return found;
    }
    // Fallback to position if available
    if (attachment.position) {
      return { imageId: currentImageId, point: attachment.position };
    }
    
    return null;
  }, [attachment, currentImageId]);

  const circleDiameter = 10;
  const verticalOffset = labelYOffset ?? 0;

  const { circleX, circleY } = useMemo(() => {
    if (!imageCoords) return { circleX: 0, circleY: 0 };
    // Compute rendered-space coords from natural coords
    // We're inside the overlay container, so don't add offsets to the circle position
    const renderedY = imageCoords.point.y * initialImageScale;
    const renderedX = (referenceLineTop && referenceLineBottom)
      ? calculateXforY(renderedY, referenceLineBottom, referenceLineTop)
      : imageCoords.point.x * initialImageScale;
    return {
      circleX: renderedX,
      circleY: renderedY,
    };
  }, [imageCoords, initialImageScale, referenceLineTop, referenceLineBottom]);

  const updatePositions = useCallback(() => {
    if (nameRef.current) {
      const nameHeight = nameRef.current.clientHeight;
      const nameWidth = nameRef.current.offsetWidth;
      
      const scale = currentTransform.scale;
      const scaledNameHeight = nameHeight / scale;
      const scaledNameWidth = nameWidth / scale;
      
      // Calculate where the image right edge is on screen
      const scaledMargin = initialHorizontalOffset * scale;
      const scaledImage = imageContainerWidth * scale;
      const imageRightEdgeScreen = scaledImage + currentTransform.x + scaledMargin;
      const distanceFromRightEdge = containerWidth - imageRightEdgeScreen;
      
      const isImagePastRight = distanceFromRightEdge < 0;
      
      // Position labels just past the image edge (with small margin)
      const LABEL_MARGIN = 10; // Fixed margin from image edge
      
      const textBoxRight = isImagePastRight 
        ? Math.abs(distanceFromRightEdge) / scale + LABEL_MARGIN
        : LABEL_MARGIN;
      
      const textBoxTop = (circleY + verticalOffset) - (scaledNameHeight / 2);
      
      // Line should end at the left edge of the label
      // textBoxRight is the distance from the right edge of container
      // So the left edge of the label is at: containerWidth - textBoxRight - scaledNameWidth
      const labelLeftEdge = imageContainerWidth - textBoxRight - scaledNameWidth;
      
      setPositions({
        textBox: { right: textBoxRight + 10, top: textBoxTop },
        line: {
          left: circleX,
          right: labelLeftEdge,
          top: circleY + verticalOffset - 1,
          verticalTop: Math.min(circleY, circleY + verticalOffset) - 1,
          verticalHeight: Math.abs(verticalOffset)
        }
      });
    }
  }, [currentTransform, containerWidth, imageContainerWidth, circleY, circleX, initialHorizontalOffset, verticalOffset]);

  useEffect(() => {
    updatePositions();
  }, [updatePositions]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!onPositionChange) return;

    e.stopPropagation();
    setIsDragging(true);
    setHasDragged(false);
    const startY = e.clientY;
    const startImageY = imageCoords?.point.y || 0;

    const handleMouseMove = (e: MouseEvent) => {
      e.stopPropagation();
      setHasDragged(true);
      const currentY = e.clientY;
      
      // Calculate the change in Y position in screen pixels
      const pixelDeltaY = currentY - startY;
      
      // Convert pixel movement to image coordinate system
      const imageDeltaY = pixelDeltaY / (initialImageScale * currentTransform.scale);
      
      // Calculate the new Y position in the image coordinate system
      const newY = startImageY + imageDeltaY;
                  
      onPositionChange(attachment.id, currentImageId, newY);
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.stopPropagation();
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasDragged && onClick) {
      onClick(attachment.id, e);
    }
    setHasDragged(false);
  };

  const getAttachmentStyle = useCallback(() => {
    const relatedGuys = attachment.relatedGuys;
    const relatedSpans = attachment.relatedSpans;
    
    if (relatedGuys && relatedGuys.length > 0) {
      return {
        color: 'rgba(255, 215, 0, ', // Gold color for guy attachments
        icon: '↓' // Down arrow for guy wires
      };
    }
    if (attachment.type === 'wire') {
      return {
        color: 'rgba(147, 197, 253, ', // Light blue for wire attachments
        icon: '⚡' // Lightning for wires
      };
    }
    if (relatedSpans && relatedSpans.length > 0) {
      return {
        color: 'rgba(147, 197, 253, ', // Light blue for span attachments
        icon: '⚡' // Lightning for power spans
      };
    }
    return {
      color: attachment.classification === '' ? 'rgba(255, 0, 0, ' : 'rgba(255, 255, 255, ',
      icon: null
    };
  }, [attachment]);

  const getTextColor = useCallback(() => {
    const relatedGuys = attachment.relatedGuys;
    const relatedSpans = attachment.relatedSpans;
    
    if ((relatedGuys && relatedGuys.length > 0) || 
        (relatedSpans && relatedSpans.length > 0)) {
      return 'rgba(0, 0, 0, ';
    }
    return attachment.classification === '' ? 'rgba(255, 255, 255, ' : 'rgba(0, 0, 0, ';
  }, [attachment]);

  const formatMeasurement = (value: number, format: PoleFormatOption): string => {
    // value is in meters
    if (format === 'metric') {
      return `${value.toFixed(1)}m`;
    } else if (format === 'feet_inches') {
      const totalFeet = value * 3.28084;
      const feet = Math.floor(totalFeet);
      const inches = Math.round((totalFeet - feet) * 12);
      return `${feet}'${inches}"`;
    } else {
      const feet = value * 3.28084;
      return `${feet.toFixed(1)}ft`;
    }
  };

  const renderClassification = useCallback(() => {
    const attachmentStyle = getAttachmentStyle();
    const inventoryClass = attachment.inventoryClass;
    const displayName = inventoryClass?.name || attachment.classification || attachment.label || "select name";
    const subcategories = inventoryClass?.subcategories;
    const isManual = attachment.isManualMeasurement === "true";
    const hasNoName = !inventoryClass?.name && !attachment.classification;

    return (
      <div 
        style={{
          ...styles.flexCol,
          position: 'relative',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={{ ...styles.flexRow, gap: '8px' }}>
          <div style={{
            ...styles.textSmall,
            ...styles.fontSemibold,
            ...styles.truncate,
            ...styles.flexRow,
            maxWidth: '120px',
            ...(hasNoName ? {
              backgroundColor: '#ef4444',
              color: '#ffffff',
              paddingLeft: '8px',
              paddingRight: '8px',
              paddingTop: '2px',
              paddingBottom: '2px',
              borderRadius: '4px',
            } : {}),
          }}>
            {attachmentStyle.icon && <span style={{ marginRight: '4px' }}>{attachmentStyle.icon}</span>}
            {displayName}
          </div>
          <div style={{ ...styles.flexRow, gap: '4px' }}>
            <div style={{
              ...styles.textTiny,
              ...styles.fontBold,
              backgroundColor: '#2563eb',
              color: '#ffffff',
              paddingLeft: '6px',
              paddingRight: '6px',
              paddingTop: '2px',
              paddingBottom: '2px',
            }}>
              {formatMeasurement(attachment.height, measurementFormat)}
            </div>
            {isManual && (
              <div 
                style={{
                  ...styles.textTiny,
                  ...styles.fontBold,
                  ...styles.rounded,
                  backgroundColor: '#f97316',
                  color: '#ffffff',
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  paddingTop: '2px',
                  paddingBottom: '2px',
                }}
                title="Manual measurement"
              >
                M
              </div>
            )}
          </div>
        </div>
        
        {/* Hover content */}
        {subcategories && subcategories.length > 0 && (
          <div 
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              zIndex: 999,
              backgroundColor: '#ffffff',
              ...styles.border,
              ...styles.roundedLg,
              ...styles.shadowLg,
              opacity: isHovered ? 1 : 0,
              visibility: isHovered ? 'visible' : 'hidden',
              transition: 'all 200ms',
              padding: '12px',
              minWidth: '220px',
              marginTop: '4px',
              transformOrigin: 'top right',
            }}
          >
            {/* Header */}
            <div style={{
              ...styles.borderBottom,
              paddingBottom: '8px',
              marginBottom: '8px',
            }}>
              <div style={{
                ...styles.flexRow,
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500,
                ...(hasNoName ? {
                  backgroundColor: '#ef4444',
                  color: '#ffffff',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  borderRadius: '4px',
                } : {}),
              }}>
                {attachment.type === 'wire' && <span>⚡</span>}
                {displayName}
              </div>
              <div style={{
                ...styles.textSmall,
                ...styles.flexRow,
                color: '#2563eb',
                marginTop: '4px',
                gap: '8px',
              }}>
                Height: {formatMeasurement(attachment.height, measurementFormat)}
                {isManual && (
                  <span style={{
                    ...styles.textTiny,
                    ...styles.fontBold,
                    ...styles.rounded,
                    backgroundColor: '#f97316',
                    color: '#ffffff',
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                  }}>
                    Manual
                  </span>
                )}
              </div>
            </div>
            
            {/* Subcategories */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {subcategories.map((sub: any, index: number) => (
                <div key={index} style={{
                  ...styles.flexRow,
                  ...styles.textSmall,
                  justifyContent: 'space-between',
                }}>
                  <span style={{ color: '#6b7280' }}>{sub.name}</span>
                  <span style={{
                    fontWeight: 500,
                    color: '#111827',
                    backgroundColor: '#f3f4f6',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    borderRadius: '4px',
                  }}>
                    {sub.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [attachment, measurementFormat, getAttachmentStyle, getTextColor, formatMeasurement, isHovered]);

  if (!imageCoords) return null;

  const style = getAttachmentStyle();

  return (
    <>
      {/* Curved leader line using SVG path */}
      <svg
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      >
        {(() => {
          const nameWidth = nameRef.current ? nameRef.current.offsetWidth / currentTransform.scale : 0;
          const nameHeight = nameRef.current ? nameRef.current.clientHeight / currentTransform.scale : 0;

          // label position calculation
          // textBox.right is from the right edge of the image container
          // We need to calculate where the label actually is
          const labelLeft = imageContainerWidth - positions.textBox.right - nameWidth;
          const labelRight = labelLeft + nameWidth;
          const labelCenterY = positions.textBox.top + nameHeight / 2;

          const startX = circleX;
          const startY = circleY;
          const endX = labelLeft - 2; // small inset so stroke meets label border
          const endY = labelCenterY;

          const controlX = (startX + endX) / 2;
          const controlY = startY;

          const d = `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;

          return (
            <path
              d={d}
              stroke={`${style.color}${opacity})`}
              strokeWidth={2 / currentTransform.scale}
              fill="none"
            />
          );
        })()}
      </svg>
      
      {/* Attachment dot */}
      <div 
        style={{
          position: "absolute",
          left: `${circleX - circleDiameter / 2}px`,
          top: `${circleY - circleDiameter / 2}px`,
          width: `${circleDiameter}px`,
          height: `${circleDiameter}px`,
          transform: `scale(${1 / currentTransform.scale})`,
          backgroundColor: `${style.color}${opacity})`,
          cursor: onPositionChange ? 'ns-resize' : 'default',
          borderRadius: '9999px',
          pointerEvents: 'auto',
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      />
      
      {/* Label */}
      <div
        ref={nameRef}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '4px',
          position: "absolute",
          right: `${positions.textBox.right}px`,
          top: `${positions.textBox.top}px`,
          transform: `scale(${1 / currentTransform.scale})`,
          transformOrigin: 'right top',
          backgroundColor: `${style.color}${opacity})`,
          color: `${getTextColor()}${opacity})`,
          opacity: opacity,
          padding: '2px 4px',
          borderRadius: '5px',
          maxWidth: '200px',
          cursor: isDragging ? 'grabbing' : 'pointer',
          zIndex: isDragging ? 9999 : 10,
          pointerEvents: 'auto',
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onMouseOver={() => {
          if (nameRef.current) {
            nameRef.current.style.zIndex = '9999';
          }
        }}
        onMouseOut={() => {
          if (nameRef.current) {
            nameRef.current.style.zIndex = isDragging ? '9999' : '10';
          }
        }}
      >
        {renderClassification()}
      </div>
    </>
  );
};